// Centralized order status labels, waiter-flow transitions, and extra-batch helpers.
// Roles: waiter confirms / serves / pays; kitchen cooks (CONFIRMED→IN_KITCHEN→READY).

export const STATUS_LABEL = {
    PENDING: 'NOUĂ',
    CONFIRMED: 'TRIMISĂ BUCĂTĂRIE',
    IN_KITCHEN: 'PREGĂTIRE',
    READY: 'PREGĂTITE PENTRU SERVIRE',
    SERVED: 'SERVITĂ',
    PAID: 'PLĂTITĂ',
    CANCELLED: 'ANULATĂ',
};

export function statusLabel(status) {
    return STATUS_LABEL[status] || status;
}

// Only the WAITER's transitions. Kitchen steps happen on the KDS page.
// READY→SERVED is intentionally NOT here: serving is done per-item via the
// /r/orders/{id}/serve-item endpoint, which auto-bumps status to SERVED once
// every item is marked served. Avoids the «marked served on Comenzi while
// items are still pending on Ospatar» inconsistency.
export const WAITER_FLOW = {
    PENDING: ['CONFIRMED', 'CANCELLED'],
    CONFIRMED: ['CANCELLED'],
    READY: [],
    SERVED: ['PAID'],
};

export const NEXT_LABEL = {
    CONFIRMED: 'Confirmă comanda',
    SERVED: 'Marchează servită',
    PAID: 'Încasează',
    CANCELLED: 'Anulează',
};

// --- Extra batch: items the client added after the order left NOUĂ ---
// extra_state: new (await waiter) → sent (await kitchen) → ready (await waiter) → served
export function extraItems(order, state) {
    const items = (order?.items || []).filter((it) => it.is_extra && it.source === 'client');
    if (!state) return items;
    return items.filter((it) => (it.extra_state || 'new') === state);
}

export function extraCount(order, state) {
    return extraItems(order, state).reduce((s, it) => s + (Number(it.quantity) || 1), 0);
}

export function hasExtra(order, state) {
    return extraItems(order, state).length > 0;
}

// Any client extra not yet fully served (new/sent/cooking/ready) — blocks closing the bill.
export function hasPendingExtra(order) {
    return ['new', 'sent', 'cooking', 'ready'].some((s) => hasExtra(order, s));
}

// ----- Kitchen routing helpers (per-item needs_kitchen flag) -----

export function isKitchenItem(item) {
    // Default true for legacy items / categories without the field.
    return item?.needs_kitchen !== false;
}

// Returns indexes (into order.items) of the main-wave items that need waiter serving.
// Groups: 'kitchen' (cooked items now READY) | 'non_kitchen' (drinks/desserts) | 'all'.
export function readyToServeIndexes(order, group = 'all') {
    const items = order?.items || [];
    const out = [];
    items.forEach((it, i) => {
        if (it.is_extra) return;
        if (it.serve_state !== 'ready') return;
        const k = isKitchenItem(it);
        if (group === 'all' || (group === 'kitchen' && k) || (group === 'non_kitchen' && !k)) out.push(i);
    });
    return out;
}

export function readyToServeCount(order, group = 'all') {
    const idx = readyToServeIndexes(order, group);
    const items = order?.items || [];
    return idx.reduce((s, i) => s + (Number(items[i]?.quantity) || 1), 0);
}

// Items still waiting on the kitchen (sent + cooking) — for the waiter card hint.
// Only meaningful once the waiter has actually confirmed the order: while PENDING,
// nothing is in the kitchen yet, so we report 0.
export function waitingOnKitchenCount(order) {
    if (!['CONFIRMED', 'IN_KITCHEN'].includes(order?.status)) return 0;
    const items = order?.items || [];
    return items.reduce((s, it) => {
        if (it.is_extra || !isKitchenItem(it)) return s;
        return it.serve_state === 'ready' || it.serve_state === 'served' ? s : s + (Number(it.quantity) || 1);
    }, 0);
}

// True if at least one main-wave item routes through the kitchen.
export function orderHasKitchenItems(order) {
    return (order?.items || []).some((it) => !it.is_extra && isKitchenItem(it));
}
